/**
 * Wire message-type registry. (§2.8, §12.6)
 *
 * Every frame on the wire is `[u8 type][payload]`. The type byte is byte 0 so a
 * dispatcher can route a packet without spinning up a BitReader — exactly what
 * bitwriter.ts's header promises for `peekPacketType`. All bit-packed payloads
 * are written LSB-first, so writing the 8-bit type first lands it in byte 0 and
 * leaves the payload byte-aligned starting at byte 1 (`bytes.subarray(1)`).
 *
 * The payload codecs live next door (input.ts, welcome.ts, snapshot.ts) and, by
 * the same convention snapshot.ts already uses, take a BitWriter/BitReader and
 * (de)serialize ONLY the payload. The caller adds the type byte with
 * `beginFrame()` and reads it back with `peekPacketType()`.
 *
 * CHANGING A TYPE NUMBER IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION (§12.6).
 */

import { BitWriter } from './bitwriter.js';

/**
 * One leading u8 per frame. Client→server IDs are grouped in 0x0x, server→client
 * in 0x1x, purely for legibility — the transport does not care.
 */
export const enum MessageType {
  // ── Client → Server ──
  /** First frame after connect. Payload is RAW utf8 join-token bytes — NOT
   *  bit-packed, NOT JSON (§9.4 handoff). See encodeClientHello in welcome.ts. */
  ClientHello = 0x01,
  /** Bit-packed InputCommand batch with redundancy (§2.3). See input.ts. */
  InputBatch = 0x02,
  /** Ask the server to send a full keyframe next tick (baseline recovery, §2.8). */
  FullSnapshotRequest = 0x12,
  /** Round-trip latency probe; server answers Pong. */
  Ping = 0x14,

  // ── Server → Client ──
  /** Handshake accepted: everything needed to build the identical WorldState. */
  Welcome = 0x10,
  /** Delta-or-full world snapshot (§2.8). Payload is the snapshot.ts wire format. */
  Snapshot = 0x11,
  /** Answer to Ping; echoes the client's nonce. */
  Pong = 0x13,
  /** Handshake refused (bad/expired token, room full). Payload: reason u8. */
  Reject = 0x15,
  /**
   * The server changed which PlayerId this connection drives (§4.8): a manual
   * switch it honoured, an auto-switch on possession change, or a drop-in. The
   * client rebases prediction to the new id and blends the camera. Payload is the
   * control.ts wire format. Server is authoritative for the driven id, exactly as
   * it is for everything else — the client learns its seat here, it does not pick
   * it (welcome.ts assigns the first one; this reassigns it).
   */
  ControlAssign = 0x16,
  /**
   * The referee just whistled a restart or showed a card (§5.6). Payload is the
   * refsignal.ts wire format.
   *
   * This is the ONE rule "event" on the wire, and refsignal.ts's header argues
   * the case at length: a whistle and a card-shown are EDGES that no snapshot can
   * reconstruct (a 20Hz delta cannot see a restart stage that flips and is gone
   * inside one interval, and a card's only trace afterwards is its consequence),
   * whereas a goal or a phase change genuinely IS in the snapshot's match state
   * and stays there — which is why nothing else joined it here.
   *
   * Presentation-only: dropping this frame leaves the match bit-identical.
   */
  RefereeSignal = 0x17,
}

/** Reason codes carried by a Reject frame. */
export const enum RejectReason {
  BadToken = 0,
  Expired = 1,
  Malformed = 2,
  RoomFull = 3,
  SlotTaken = 4,
  ProtocolMismatch = 5,
  RoomClosed = 6,
}

/**
 * Read the message type of a received frame without decoding it. Returns -1 for
 * an empty buffer — a hostile zero-length frame must never index out of bounds
 * (§2.9). Byte 0 IS the type because payloads are written LSB-first (see header).
 */
export function peekPacketType(bytes: Uint8Array): number {
  return bytes.length > 0 ? bytes[0]! : -1;
}

/**
 * Start a bit-packed frame: reset the writer and lay down the type byte. The
 * caller then invokes the payload codec (encodeInputBatch, encodeWelcome,
 * encode*Snapshot) on the SAME writer, and finishes with `w.finishToBytes()`.
 *
 * Because the type is exactly 8 bits, the payload that follows begins on the
 * byte-1 boundary, so the receiver decodes it from `frame.subarray(1)`.
 */
export function beginFrame(w: BitWriter, type: MessageType): void {
  w.reset();
  w.writeBits(type, 8);
}

/**
 * The payload view of a received bit-packed frame: everything after the type
 * byte, byte-aligned. Pass this to a BitReader for the matching payload codec.
 * (ClientHello is raw bytes and uses decodeClientHello instead.)
 */
export function framePayload(bytes: Uint8Array): Uint8Array {
  return bytes.subarray(1);
}
